import Images from "../utils/Images";
import { FAQ_ITEMS } from "./faqs";
import { MARKET_PORTAL } from "./simmarkets";

export const VERIFY_NAV = [
  { id: "intro", label: "Introduction" },
  { id: "why-simverify", label: "Why it matters" },
  { id: "vision", label: "Vision" },
  { id: "engines", label: "Engines" },
  { id: "journey", label: "How it works" },
  { id: "industries", label: "Industries" },
  { id: "videos", label: "Videos" },
  { id: "faqs", label: "FAQs" },
  { id: "demo", label: "Get started" },
];

export const SIGNALS = [
  { n: "01", title: "Borrower", line: "Identity and stability before the facility is booked." },
  { n: "02", title: "Vehicle", line: "Ownership, encumbrance and collateral fitness." },
  { n: "03", title: "Device", line: "IMEI, financing status and blacklist checks." },
  { n: "04", title: "SIM", line: "Behavioural signals that credit files never show." },
];

export const INTRO_BEATS = [
  {
    n: "01",
    title: "Limited files",
    copy: "Traditional verification often relies on incomplete information and fragmented systems.",
  },
  {
    n: "02",
    title: "Manual exposure",
    copy: "Slow, disconnected checks leave institutions open to fraud and inaccurate risk.",
  },
  {
    n: "03",
    title: "One trust profile",
    copy: "SimVerify unifies borrower, asset, device and SIM intelligence before the decision.",
  },
];

export const CHALLENGES = [
  "Lending fraud",
  "Identity fraud",
  "Asset fraud",
  "Duplicate financing",
  "Loan defaults",
  "Incomplete customer verification",
  "Slow approval processes",
  "Poor visibility into borrower behaviour",
];

export const OBJECTIVE_GROUPS = [
  {
    id: "verify",
    n: "01",
    title: "Verify",
    copy: "See the person and the asset clearly.",
    items: [
      "Verify borrower identities more effectively.",
      "Authenticate vehicle ownership and legitimacy.",
      "Verify electronic devices and financing status.",
      "Analyse SIM behaviour to identify lending risks.",
    ],
  },
  {
    id: "decide",
    n: "02",
    title: "Decide",
    copy: "Score risk, then move.",
    items: [
      "Generate AI-powered Trust Scores.",
      "Detect fraud before approval.",
      "Improve credit quality.",
      "Accelerate lending decisions.",
    ],
  },
  {
    id: "protect",
    n: "03",
    title: "Protect",
    copy: "Keep the book and the relationship sound.",
    items: [
      "Reduce loan defaults.",
      "Improve customer confidence.",
      "Strengthen compliance.",
      "Enable secure enterprise integrations.",
    ],
  },
];

export const ENGINES = [
  {
    id: "vehicle",
    n: "01",
    title: "Vehicle Verification Engine",
    kicker: "Collateral",
    copy: "Verify the legitimacy and financial status of vehicles before accepting them as collateral.",
    art: Images.verifyEngineVehicle,
    alt: "Illustration of vehicle identity, ownership and collateral checks",
    spec: "Ultra-wide banner 24:5 · 2400×500 · Editorial illustration of a West African vehicle being inspected as collateral — VIN plate, registration and a calm underwriter. Keep the subject in the centre third; top and bottom will crop. Palette: navy #100E34, gold #FFBF18, royal #4F48EC, cyan #22D3EE. No logos or readable UI. File: /images/simverify/engine-vehicle.png",
    items: [
      "VIN Verification",
      "Chassis Verification",
      "Engine Verification",
      "Registration Validation",
      "Ownership Verification",
      "Previous Ownership History (where available)",
      "Vehicle Valuation",
      "Outstanding Financing Check",
      "Encumbrance Check",
      "Theft Database Verification",
      "Insurance Verification",
      "Vehicle Risk Report",
      "Collateral Suitability Assessment",
    ],
  },
  {
    id: "device",
    n: "02",
    title: "Device Verification Engine",
    kicker: "Electronics",
    copy: "Prevent financing fraud through comprehensive electronic device verification.",
    art: Images.verifyEngineDevice,
    alt: "Illustration of a smartphone IMEI and financing check",
    spec: "Ultra-wide banner 24:5 · 2400×500 · Illustration of a smartphone and IMEI/device dossier on a light ice desk. Keep the subject in the centre third. Navy, gold #FFBF18, royal #4F48EC, cyan #22D3EE. No logos. File: /images/simverify/engine-device.png",
    items: [
      "IMEI Verification",
      "Device Ownership",
      "Device Financing Status",
      "Blacklist Verification",
      "Lost or Stolen Device Detection",
      "Manufacturer Verification",
      "Device Valuation",
      "Device Risk Reports",
    ],
  },
  {
    id: "sim",
    n: "03",
    title: "SIM Behaviour Intelligence",
    kicker: "Behaviour",
    copy: "Understand customer behaviour beyond traditional credit reports. These insights help identify potential fraud risks before lending decisions are made.",
    art: Images.verifyEngineSim,
    alt: "Illustration of SIM lifecycle and behavioural intelligence",
    spec: "Ultra-wide banner 24:5 · 2400×500 · Abstract-but-human illustration of a SIM card, linked devices and a calm behavioural timeline. Keep the subject in the centre third. Navy, gold, royal, cyan. No logos. File: /images/simverify/engine-sim.png",
    items: [
      "SIM Stability",
      "SIM Lifecycle",
      "SIM Replacement History",
      "Linked Devices",
      "Linked Borrowers",
      "Borrowing Behaviour Patterns",
      "Dormancy Analysis",
      "Behavioural Risk Indicators",
    ],
  },
  {
    id: "trust",
    n: "04",
    title: "AI Trust Intelligence Engine",
    kicker: "Decision",
    copy: "The intelligence behind every decision. SimVerify combines all verification signals into one unified AI-powered Trust Score, with supporting insights so decision-makers can see the rationale.",
    art: Images.verifyEngineTrust,
    alt: "Illustration of an explainable AI trust decision",
    spec: "Ultra-wide banner 24:5 · 2400×500 · Illustration of an explainable trust decision — layered signals resolving into a shield with a green check, not a fake numeric score. Keep the subject in the centre third. Navy, gold #FFBF18, royal #4F48EC, check green #4ADE80. No logos or digits. File: /images/simverify/engine-trust.png",
    items: [
      "AI Trust Score",
      "Asset Confidence Score",
      "Borrower Stability Score",
      "Fraud Probability Score",
      "Lending Recommendation",
      "Explainable AI Decision Report",
    ],
  },
];

export const FEATURES = [
  "Identity & Customer Lookup",
  "Vehicle Verification",
  "Device Verification",
  "SIM Behaviour Analytics",
  "AI Trust Scoring",
  "Fraud Alerts",
  "Verification Certificates",
  "Portfolio Monitoring",
  "Search History",
  "Executive Dashboards",
  "Reporting & Analytics",
  "API Integration Gateway",
  "Multi-Tenant Architecture",
  "Audit Trails",
  "Secure Administration",
];

export const BENEFIT_GROUPS = [
  {
    id: "banks",
    n: "01",
    title: "Banks & financial institutions",
    copy: "Stronger underwriting with a complete view of borrower and asset risk.",
    items: [
      "Reduce lending fraud.",
      "Improve loan quality.",
      "Accelerate approvals.",
      "Strengthen underwriting decisions.",
      "Improve customer confidence.",
      "Enhance regulatory compliance.",
      "Reduce operational risk.",
      "Improve portfolio performance.",
    ],
  },
  {
    id: "fintech",
    n: "02",
    title: "FinTech companies",
    copy: "Instant verification for digital lending without a heavier desk.",
    items: [
      "Verify borrowers instantly.",
      "Automate risk assessment.",
      "Improve digital lending decisions.",
      "Reduce defaults.",
      "Improve customer onboarding.",
    ],
  },
  {
    id: "insurance",
    n: "03",
    title: "Insurance companies",
    copy: "Confirm the asset and the owner before the policy or the claim.",
    items: [
      "Verify insured assets.",
      "Validate ownership.",
      "Improve underwriting quality.",
      "Reduce fraudulent claims.",
    ],
  },
  {
    id: "asset",
    n: "04",
    title: "Asset finance & retailers",
    copy: "Stop duplicate financing before the asset leaves the floor.",
    items: [
      "Verify financed devices and vehicles.",
      "Prevent duplicate financing.",
      "Validate collateral.",
      "Improve asset recovery confidence.",
    ],
  },
  {
    id: "consumers",
    n: "05",
    title: "Consumers",
    copy: "Legitimate customers move faster, with a fairer, clearer decision.",
    items: [
      "Faster approvals.",
      "Fairer lending decisions.",
      "Improved trust.",
      "Better customer experience.",
      "Greater transparency.",
    ],
  },
];

export const JOURNEY = [
  {
    n: "01",
    title: "Customer information submitted",
    copy: "The institution submits a borrower identifier, VIN, IMEI or other required details.",
  },
  {
    n: "02",
    title: "Identity validation",
    copy: "The system validates the information and prepares verification requests.",
  },
  {
    n: "03",
    title: "Intelligent data retrieval",
    copy: "SimVerify retrieves and normalizes information from connected data sources.",
  },
  {
    n: "04",
    title: "Multi-layer verification",
    copy: "Vehicle, device, borrower and behavioural verification are performed simultaneously.",
  },
  {
    n: "05",
    title: "AI trust analysis",
    copy: "The AI Trust Engine analyses all available signals and applies configurable business rules.",
  },
  {
    n: "06",
    title: "Risk assessment",
    copy: "Potential fraud indicators and transaction risks are identified.",
  },
  {
    n: "07",
    title: "Trust score generated",
    copy: "An AI-powered Trust Score and supporting recommendations are produced.",
  },
  {
    n: "08",
    title: "Decision support",
    copy: "Results are presented through dashboards, reports and digital verification certificates.",
  },
  {
    n: "09",
    title: "Enterprise integration",
    copy: "Institutions use the insights within their existing lending or operational workflows.",
  },
];

export const JOURNEY_PHASES = [
  { id: "submit", n: "01", title: "Submit", copy: "The request is received and prepared.", steps: [0, 1, 2] },
  { id: "analyse", n: "02", title: "Analyse", copy: "Signals run together, then risk is named.", steps: [3, 4, 5] },
  { id: "decide", n: "03", title: "Decide", copy: "Score, certify, then hand off to the desk.", steps: [6, 7, 8] },
];

export const INDUSTRIES = [
  { name: "Commercial Banks", icon: "landmark" },
  { name: "Rural Banks", icon: "building" },
  { name: "Savings & Loans Companies", icon: "wallet" },
  { name: "Credit Unions", icon: "users" },
  { name: "Microfinance Institutions", icon: "coins" },
  { name: "FinTech Lenders", icon: "smartphone" },
  { name: "Insurance Companies", icon: "shield" },
  { name: "Vehicle Dealers", icon: "car" },
  { name: "Auto Leasing Companies", icon: "car" },
  { name: "Hire Purchase Companies", icon: "handshake" },
  { name: "Mobile Device Financing Companies", icon: "cpu" },
  { name: "Electronics Retailers", icon: "cpu" },
  { name: "Telecom Operators", icon: "radio" },
  { name: "Credit Reference Bureaus", icon: "file" },
  { name: "Pawn Brokers", icon: "gem" },
];

export const INTEGRATIONS = [
  "Credit Reference Bureaus",
  "Commercial Banks",
  "Savings & Loans Systems",
  "Mobile Network Operators",
  "DVLA",
  "Ghana Police Service",
  "GSMA IMEI Database",
  "Insurance Companies",
  "Vehicle Valuation Services",
  "API Gateway",
  "Identity & Access Management",
  "Notification Gateway",
];

export const FUTURE_INTEGRATIONS = [
  "National Identification Authority (subject to applicable approvals)",
  "GPS & Telematics",
  "Property Verification",
  "Employment Verification",
  "Open Banking",
  "Cross-Border Verification",
];

export const DIFFERENTIATORS = [
  "Borrower Intelligence",
  "Asset Verification",
  "Behavioural Analytics",
  "AI Trust Scoring",
  "Fraud Detection",
  "Enterprise Integration",
  "Explainable Decision Support",
];

export const VIDEOS = [
  { id: "intro", title: "Introduction to SimVerify", kind: "Overview" },
  { id: "vehicle", title: "Vehicle Verification Walkthrough", kind: "Operations" },
  { id: "device", title: "Device Verification Demo", kind: "Operations" },
  { id: "sim", title: "SIM Behaviour Intelligence", kind: "Intelligence" },
  { id: "score", title: "AI Trust Score Explained", kind: "Intelligence" },
  { id: "fraud", title: "Fraud Detection Scenarios", kind: "Risk" },
  { id: "dashboard", title: "Enterprise Dashboard Overview", kind: "Analytics" },
  { id: "api", title: "API Integration Guide", kind: "Developers" },
  { id: "reporting", title: "Executive Reporting & Analytics", kind: "Analytics" },
  { id: "practice", title: "Best Practices for Digital Trust", kind: "Practice" },
];

export const VERIFY_FAQS = FAQ_ITEMS.filter((item) => item.categoryId === "simverify");

export const INTEREST_AREAS = [
  "Digital Trust Strategy",
  "Enterprise Integration",
  "Lending Risk Management",
  "Verification Workflows",
  "API Services",
  "Custom Solution Design",
  "Vehicle Verification",
  "Device Verification",
  "SIM Behaviour Intelligence",
  "AI Trust Scoring",
];

export const ORG_TYPES = [
  "Commercial Bank",
  "Rural Bank",
  "Savings & Loans",
  "Microfinance",
  "Credit Union",
  "Insurance",
  "FinTech Lender",
  "Asset Finance",
  "Vehicle Dealer",
  "Electronics Retailer",
  "Telecom",
  "Other",
];

export const APPOINTMENT_TYPES = [
  "Digital Trust Strategy",
  "Enterprise Integration",
  "Lending Risk Management",
  "Verification Workflows",
  "API Services",
  "Custom Solution Design",
];

export const CONTACT_CHANNELS = ["Call Sales", "Email Sales", "WhatsApp Business", "Live Chat"];

export const RELATED = [
  {
    id: "simmarket",
    name: "SimMarket",
    index: "01",
    copy: "Acquire qualified, educated and intent-driven customers through Africa's intelligent financial marketplace.",
    href: MARKET_PORTAL,
    cta: "Explore SimMarket",
    logo: Images.simmarket_colored,
  },
  {
    id: "simcollect",
    name: "SimCollect",
    index: "02",
    copy: "Protect your lending portfolio with intelligent debt recovery, customer engagement and repayment automation.",
    cta: "Explore SimCollect",
    logo: Images.simcollect_logo,
  },
];

export const HERO_MEDIA = {
  src: Images.verifyHero,
  alt: "A West African underwriter reviewing borrower and asset verification in a cool, well-lit workspace",
  spec: "Portrait 4:5 · 1600×2000 · Editorial photograph composed for a shield-shaped crop. Subject is a West African credit or risk professional, mid-shot, occupying the centre of frame. Cool daylight, ice-blue environment. Accents only: gold #FFBF18, royal #4F48EC, cyan #22D3EE. No logos, no readable screens, no numeric scores. File: /images/simverify/hero.png",
};

export const INTRO_MEDIA = {
  src: Images.verifyIntro,
  alt: "A verification specialist comparing borrower identity with a vehicle and device file",
  spec: "Landscape 16:10 · 1920×1200 · Two-subject editorial: a West African analyst at a light ice desk with a vehicle key fob and a smartphone in frame, suggesting borrower-plus-asset checks. Navy, gold #FFBF18, royal #4F48EC. No logos or readable UI. File: /images/simverify/intro.png",
};

export const VIDEO_MEDIA = {
  src: Images.verifyVideo,
  alt: "SimVerify product demonstration still",
  spec: "Landscape 16:9 · 1920×1080 · Product demo still of a digital trust workspace — layered signals, not a fake scoreboard. Gold #FFBF18, royal #4F48EC, cyan #22D3EE. No small UI text. File: /images/simverify/video.png",
};
